import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Maximize2, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function DeliveryReportsWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const data = await apiRequest('/warehouse/delivery-reports?limit=3');
      setReports(data || []);
    } catch (error) {
      console.error('Failed to fetch delivery reports');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="delivery-reports-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-emerald-500" />
            <CardTitle className="text-lg">Recent Deliveries</CardTitle>
          </div>
          {expandable && (
            <Button variant="ghost" size="icon" onClick={() => navigate(expandPath || '/warehouse/delivery-reports')} className="h-8 w-8">
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-4 text-slate-400">
            <Truck className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No recent deliveries</p>
          </div>
        ) : (
          <div className="space-y-2">
            {reports.map(report => (
              <div 
                key={report.id} 
                className={`p-2 rounded border flex items-center gap-2 ${
                  report.status === 'ok' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                }`}
              >
                {report.status === 'ok' ? (
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-slate-900 truncate">{report.store_name}</p>
                  <p className="text-xs text-slate-500">{report.delivery_date}</p>
                </div>
                <Badge variant={report.status === 'ok' ? 'default' : 'destructive'} className="text-xs">
                  {report.status.toUpperCase()}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
